if (!customElements.get('mowsil-category-carousel')) {
  customElements.define('mowsil-category-carousel', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.mowsil-carousel-track');
      this.slides = [...this.querySelectorAll('.mowsil-carousel-item')];
      if (!this.track || !this.slides.length) return;
      this.events = new AbortController();
      const on = (target, name, handler) => target?.addEventListener(name, handler, { signal: this.events.signal });
      this.dialog = this.querySelector('dialog');
      this.position = this.querySelector('.mowsil-carousel-position');
      this.controls = this.querySelector('.mowsil-carousel-controls');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = false;
      on(this.querySelector('[data-next]'), 'click', () => this.move(1));
      on(this.querySelector('[data-previous]'), 'click', () => this.move(-1));
      on(this.track, 'scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => this.update(), 120);
      });
      on(this.track, 'keydown', (event) => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      });
      on(this, 'pointerenter', () => { this.hovered = true; this.stop(); });
      on(this, 'pointerleave', () => { this.hovered = false; this.start(); });
      on(this, 'focusin', () => this.stop());
      on(this, 'focusout', (event) => {
        if (!this.contains(event.relatedTarget)) queueMicrotask(() => { if (this.isConnected) this.start(); });
      });
      on(document, 'visibilitychange', () => document.hidden ? this.stop() : this.start());
      on(this.reducedMotion, 'change', () => { this.stop(); this.start(); });
      on(this.querySelector('[data-pause]'), 'click', (event) => {
        this.paused = !this.paused;
        event.currentTarget.textContent = this.paused ? 'Play' : 'Pause';
        event.currentTarget.setAttribute('aria-label', this.paused ? 'Play automatic slides' : 'Pause automatic slides');
        this.paused ? this.stop() : this.start();
      });
      this.querySelectorAll('[data-mowsil-lightbox]').forEach((button) => {
        on(button, 'click', () => {
          this.stop();
          const image = this.dialog.querySelector('img');
          image.src = button.dataset.image;
          image.alt = button.dataset.alt;
          this.previousOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
          this.dialog.showModal();
        });
      });
      on(this.querySelector('.mowsil-lightbox-close'), 'click', () => this.dialog.close());
      on(this.dialog, 'click', (event) => { if (event.target === this.dialog) this.dialog.close(); });
      on(this.dialog, 'close', () => {
        document.body.style.overflow = this.previousOverflow ?? '';
        this.dialog.querySelector('img').removeAttribute('src');
        this.start();
      });
      this.resizeObserver = new ResizeObserver(() => {
        const width = this.track.clientWidth;
        if (this.trackWidth && width !== this.trackWidth) {
          const { step, max } = this.geometry();
          this.track.scrollTo({ left: Math.min(this.index || 0, max) * step, behavior: 'instant' });
        }
        this.trackWidth = width;
        this.update();
        this.stop();
        this.start();
      });
      this.resizeObserver.observe(this.track);
      this.slides.forEach((slide) => this.resizeObserver.observe(slide));
      this.update();
      this.start();
    }

    geometry() {
      const first = this.slides[0].getBoundingClientRect();
      const track = this.track.getBoundingClientRect();
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = first.width + gap;
      const visible = Math.max(1, Math.floor((this.track.clientWidth + gap + 1) / step));
      const max = Math.max(0, this.slides.length - visible);
      const offset = track.left - first.left;
      // At the end of a multi-card row the scroll offset can be less than one full step.
      const atEnd = Math.abs(this.track.scrollLeft) >= this.track.scrollWidth - this.track.clientWidth - 2;
      return { step, max, index: atEnd ? max : Math.max(0, Math.min(max, Math.round(offset / step))) };
    }

    update() {
      const { index } = this.geometry();
      this.index = index;
      if (this.controls) this.controls.hidden = this.track.scrollWidth <= this.track.clientWidth + 2;
      if (this.position) this.position.textContent = `${index + 1} / ${this.slides.length}`;
      if (this.classList.contains('mowsil-category-carousel--stories')) {
        const slide = this.slides[index];
        // Fit the current story instead of leaving space for the longest one.
        const trackStyle = getComputedStyle(this.track);
        const padding = parseFloat(trackStyle.paddingTop) + parseFloat(trackStyle.paddingBottom);
        const scrollbar = this.track.offsetHeight - this.track.clientHeight;
        this.track.style.height = `${slide.offsetHeight + padding + scrollbar}px`;
        const media = slide.querySelector('.mowsil-carousel-image-button');
        const offset = media ? media.offsetTop - slide.offsetTop + media.offsetHeight / 2 - 22 : 20;
        this.style.setProperty('--carousel-arrow-offset', `${Math.max(20, offset)}px`);
      }
    }

    move(direction) {
      const { index, step, max } = this.geometry();
      const next = index + direction;
      this.track.scrollTo({ left: (next > max ? 0 : next < 0 ? max : next) * step, behavior: this.reducedMotion.matches ? 'instant' : 'smooth' });
    }

    stop() { clearInterval(this.timer); this.timer = null; }

    start() {
      if (this.timer || this.dataset.autoplay !== 'true' || this.paused || this.hovered || this.reducedMotion.matches || document.hidden || this.dialog.open || this.contains(document.activeElement) || this.track.scrollWidth <= this.track.clientWidth + 2) return;
      this.timer = setInterval(() => this.move(1), Number(this.dataset.speed) || 4000);
    }

    disconnectedCallback() {
      this.stop();
      clearTimeout(this.scrollTimer);
      this.resizeObserver?.disconnect();
      if (this.dialog?.open) {
        document.body.style.overflow = this.previousOverflow ?? '';
        this.dialog.close();
      }
      this.events?.abort();
    }
  });
}
